// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { captureException } from "@sentry/react";
import { saveAs } from "file-saver";
import { toJS } from "mobx";
import {
  PDFBool,
  type PDFCheckBox,
  type PDFDocument,
  PDFDropdown,
  type PDFForm,
  PDFName,
  type PDFRadioGroup,
  type PDFTextField,
  StandardFonts,
} from "pdf-lib";

import { fetchWorkflowsTemplate } from "./fetchWorkflowsTemplate";

export type SetFunc = (
  fieldName: string,
  value?: string | boolean | number,
) => void;

export type PDFFillerFunc<FormData = any> = (
  formData: FormData,
  set: SetFunc,
  form: PDFForm,
  doc: PDFDocument,
) => Promise<void>;

export type FillPDFFunc = (formData: any) => Promise<Uint8Array<ArrayBuffer>>;

type PDFLib = {
  PDFCheckBox: typeof PDFCheckBox;
  PDFRadioGroup: typeof PDFRadioGroup;
  PDFTextField: typeof PDFTextField;
  PDFDocument: typeof PDFDocument;
};

const fillPDF: (
  fillerFunc: PDFFillerFunc,
  [template, pdfLib]: [ArrayBuffer, PDFLib],
) => FillPDFFunc =
  (fillerFunc: PDFFillerFunc, [template, pdfLib]: [ArrayBuffer, PDFLib]) =>
  async (formData: any) => {
    const { PDFCheckBox, PDFRadioGroup, PDFTextField, PDFDocument } = pdfLib;

    const doc = await PDFDocument.load(template);
    const form = doc.getForm();
    const set: SetFunc = (fieldName, value) => {
      const field = form.getField(fieldName); // If fieldName doesn't exist, this will throw

      if (field instanceof PDFTextField) {
        field.setText((value ?? "").toString());
      } else if (field instanceof PDFCheckBox) {
        if (value) {
          field.check();
        } else {
          field.uncheck();
        }
      } else if (
        field instanceof PDFRadioGroup ||
        field instanceof PDFDropdown
      ) {
        if (value) {
          field.select(value.toString());
        } else {
          field.clear();
        }
      }
    };

    try {
      await fillerFunc(formData, set, form, doc);
    } catch (e) {
      captureException(e);
    }

    // Firefox/PDF.js compatibility fixes
    // Ensure filled AcroForm fields render consistently in Firefox/PDF.js by embedding a font
    // and generating appearance streams, then forcing /NeedAppearances=false so viewers use
    // those baked appearances instead of trying (and failing) to synthesize them; we also save
    // with updateFieldAppearances:false to preserve the APs and avoid Firefox “blank field” issues.
    try {
      // Step 1: Embed font for appearance generation
      const font = await doc.embedFont(StandardFonts.Helvetica);

      // Step 2: Generate appearances with embedded font
      form.updateFieldAppearances(font);

      // Step 3: Set NeedAppearances to FALSE (not true!)
      form.acroForm.dict.set(PDFName.of("NeedAppearances"), PDFBool.False);
    } catch (error) {
      console.error("Error applying Firefox compatibility fixes:", error);
      // Continue anyway. Better to have a working PDF than none!
    }

    // Step 4: Save without re-generating appearances
    const pdfBytes = (await doc.save({
      updateFieldAppearances: false,
    })) satisfies Uint8Array;

    // PDFDocument.save() returns a Uint8Array<ArrayBufferLike> which could theoretically
    // be backed by a SharedArrayBuffer. In practice it's not, but to be able to create a blob
    // and save the file we need to verify this.
    if (pdfBytes.buffer instanceof ArrayBuffer) {
      return pdfBytes as Uint8Array<ArrayBuffer>;
    }

    throw new Error("PDFDocument.save() returned an unexpected data type");
  };

// fillerFunc() is the callback responsible for actually filling out the form.
// It is passed a set(fieldName, value) function for setting fields. For more
// advance manipulation, it's also passed the PDFDocument object and the necessary
// objects from the pdf-lib.
//
// After setting the fields, calling `form.flatten()` will bake the form into a
// static PDF. To generate the boilerplate needed to fill all the fields in a
// given PDF, run: `nx pdfformfiller-boilerplate staff src/api/workflowsTemplates/path/to/your.pdf`
export async function getPdfTemplate(
  templateUrl: PdfUrl,
  fillerFunc: PDFFillerFunc,
) {
  // While the template is being downloaded, we also dynamically import pdf-lib as
  // a separate code chunk. Otherwise it would add 200k to the bundle for everyone.
  const [template, pdfLib] = await Promise.all([
    fetchWorkflowsTemplate(templateUrl),
    import("pdf-lib"),
  ]);
  const { PDFDocument, PDFCheckBox, PDFRadioGroup, PDFTextField } = pdfLib;

  return fillPDF(fillerFunc, [
    template,
    { PDFCheckBox, PDFRadioGroup, PDFTextField, PDFDocument },
  ]);
}

export async function fillAndSavePDF(
  fileName: string,
  templateUrl: PdfUrl,
  fillerFunc: PDFFillerFunc,
  formData: any,
) {
  const pdfTemplate = await getPdfTemplate(templateUrl, fillerFunc);

  const pdfBytes = await pdfTemplate({ ...toJS(formData) });

  const blob = new Blob([pdfBytes]);
  saveAs(blob, fileName);
}

export async function fixRadioGroups(form: PDFForm) {
  // If a form field doesn't explicitly specify an appearance for all of its widgets'
  // effective states, pdf-lib will apply the library's default appearance for all the
  // field's widgets. However, some documents use the lack of an `Off` appearance to mean
  // that nothing should be drawn if the widget is off. This function fills in the missing
  // `Off` appearances for all RadioButtonGroups with blank ones.
  // Call it in your fillerFunc if the flattened form's RadioButtonGroups look wrong.
  const { PDFRadioGroup, PDFDict, PDFName } = await import("pdf-lib");
  const offName = PDFName.of("Off");
  form.getFields().forEach((field) => {
    if (field instanceof PDFRadioGroup) {
      field.acroField.getWidgets().forEach((widget) => {
        const { context } = widget.dict;
        const offRef = context.register(context.formXObject([]));
        const normalAppearance = widget.getNormalAppearance();
        if (
          normalAppearance instanceof PDFDict &&
          !normalAppearance.has(offName)
        ) {
          normalAppearance.set(offName, offRef);
        }
      });
    }
  });
}
